import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Download, Edit, Eye } from 'lucide-react'
import Paper from '@/components/Paper'
import {
    DataTableAction,
    DataTableCustomized,
    FilterBar,
} from '@/components/ui/data-table-customized'
import { useModal } from '@/contexts/modal'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { ContractStatus } from '@/modules/Contract/types'
import { IPermissionProps } from '@/types/shared'
import { usePartnerContracts } from '../../hooks'
import type { PartnerContract } from '../../types'

const ContractsList: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation('contract')
    const { setModal } = useModal()
    const { openFilePreview, downloadFileWithPresigned } =
        useFilePreviewWithPresigned()

    const {
        contracts,
        pagination,
        stateComponent,
        isFetching,
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        filterItems,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
    } = usePartnerContracts()

    // Handle file preview
    const handlePreview = useCallback(
        async (row: PartnerContract) => {
            const attachments = row?.attachments?.filter(
                (attachment) => !attachment?.attachment_closed
            )
            if (attachments?.length === 1) {
                await openFilePreview(attachments[0])
            } else if (attachments?.length > 1) {
                toast.error(t('has-more-one-attachment'))
            } else {
                toast.error(t('no-attachment'))
            }
        },
        [openFilePreview, t]
    )

    // Handle file download
    const handleDownload = useCallback(
        async (row: PartnerContract) => {
            const attachments = row?.attachments?.filter(
                (attachment) => !attachment?.attachment_closed
            )
            if (attachments?.length === 1) {
                await downloadFileWithPresigned(attachments[0])
            } else if (attachments?.length > 1) {
                toast.error(t('has-more-one-attachment'))
            } else {
                toast.error(t('no-attachment'))
            }
        },
        [downloadFileWithPresigned, t]
    )

    // Row actions
    const rowActions: DataTableAction<PartnerContract>[] = [
        {
            key: 'edit',
            label: t('edit'),
            icon: <Edit className="w-4 h-4" />,
            onClick: (row: PartnerContract) => {
                setModal('ContractModals/PartnerContractModal/index', {
                    partnerContract: row,
                    mode: 'update',
                })
            },
        },
        {
            key: 'preview',
            label: t('contract-preview'),
            icon: <Eye className="w-4 h-4" />,
            disabled: (row: PartnerContract) =>
                row?.contract_status !== ContractStatus.Active,
            onClick: handlePreview,
        },
        {
            key: 'download',
            label: t('download-file'),
            icon: <Download className="w-4 h-4" />,
            disabled: (row: PartnerContract) =>
                row?.contract_status !== ContractStatus.Active,
            onClick: handleDownload,
        },
    ]

    return (
        <Paper className="space-y-2 mt-2">
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
            />

            {stateComponent || (
                <DataTableCustomized
                    columns={columns}
                    rows={contracts}
                    rowKey={(row) => String(row?.contract_id)}
                    size="small"
                    resizable
                    loading={!contracts.length && !pagination}
                    isFetching={isFetching}
                    emptyMessage={t('no-data')}
                    actions={rowActions}
                    sorting={{
                        sortBy: params.sort_by,
                        sortOrder: params.sort_order,
                        onSortChange: handleSortChange,
                    }}
                    pagination={{
                        page: params.page || 1,
                        pageSize: params.page_size || 20,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        pageSizeOptions: [10, 20, 50, 100],
                    }}
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search-contract'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'partner-contracts-list',
                        },
                    }}
                />
            )}
        </Paper>
    )
}

export default ContractsList
